import { BinType } from "@/domain/models/bin";
import {
  BinMaterialEnum,
  BinSizeEnum,
  BinTaskEnum,
  CleanupTypeEnum,
  ServiceTaskTypeEnum,
} from "@/domain/models/common";
import { TasksType } from "@/domain/models/job";
import { i18n } from "@/i18n";

import { formatBinSize } from "./strings";

export const convertTaskTypeToTaskId = (
  taskType: BinTaskEnum | CleanupTypeEnum,
  defaultReturnAsNull = false,
) => {
  switch (taskType) {
    case BinTaskEnum.DROPOFF:
      return ServiceTaskTypeEnum.DROPBIN;
    case BinTaskEnum.DUMPANDRETURN:
      return ServiceTaskTypeEnum.DUMPANDRETURNBIN;
    case BinTaskEnum.EXCHANGE:
      return ServiceTaskTypeEnum.EXCHANGEBIN;
    case BinTaskEnum.PICKUP:
      return ServiceTaskTypeEnum.PICKUPBIN;
    case BinTaskEnum.WAITANDLOAD:
      return ServiceTaskTypeEnum.WAITANDLOADBIN;
    case CleanupTypeEnum.NONE:
      return ServiceTaskTypeEnum.JUNKREMOVAL;
    default:
      return defaultReturnAsNull ? null : ServiceTaskTypeEnum.JUNKREMOVAL;
  }
};

export const getBinTaskIds = () => {
  return [
    ServiceTaskTypeEnum.DROPBIN,
    ServiceTaskTypeEnum.DUMPANDRETURNBIN,
    ServiceTaskTypeEnum.EXCHANGEBIN,
    ServiceTaskTypeEnum.PICKUPBIN,
    ServiceTaskTypeEnum.WAITANDLOADBIN,
  ];
};

export const getJunkTaskIds = () => {
  return [ServiceTaskTypeEnum.JUNKREMOVAL];
};

export const getBinTaskOptions = (isCreatingBinRelatedJob?: boolean) => [
  {
    value: BinTaskEnum.DROPOFF,
    disabled: isCreatingBinRelatedJob,
    label: i18n.t("add_new_job_drawer.bin_task.drop_off"),
  },
  {
    value: BinTaskEnum.WAITANDLOAD,
    disabled: isCreatingBinRelatedJob,
    label: i18n.t("add_new_job_drawer.bin_task.wait_load"),
  },
  {
    value: BinTaskEnum.PICKUP,
    label: i18n.t("add_new_job_drawer.bin_task.pick_up"),
  },
  {
    value: BinTaskEnum.EXCHANGE,
    label: i18n.t("add_new_job_drawer.bin_task.exchange"),
  },
  {
    value: BinTaskEnum.DUMPANDRETURN,
    label: i18n.t("add_new_job_drawer.bin_task.dump_return"),
  },
];

export const getBinSizeOptions = (enabledValue?: BinSizeEnum | null) =>
  Object.values(BinSizeEnum).map(value => ({
    label: formatBinSize(value),
    value,
    disabled: enabledValue ? value !== enabledValue : false,
  }));

export const getBinMaterialOptions = ({
  emptyOptionLabel,
}: {
  emptyOptionLabel: string | undefined;
}) => {
  const BinMaterials = [
    {
      label: i18n.t("bin_materials.mixed_waste"),
      value: BinMaterialEnum.MIXED_WASTE,
    },
    {
      label: i18n.t("bin_materials.mixed_fill"),
      value: BinMaterialEnum.MIXED_FILL,
    },
    { label: i18n.t("bin_materials.soil"), value: BinMaterialEnum.SOIL },
    { label: i18n.t("bin_materials.brick"), value: BinMaterialEnum.BRICK },
    {
      label: i18n.t("bin_materials.concrete"),
      value: BinMaterialEnum.CONCRETE,
    },
    { label: i18n.t("bin_materials.asphalt"), value: BinMaterialEnum.ASPHALT },
    { label: i18n.t("bin_materials.metal"), value: BinMaterialEnum.METAL },
  ];

  if (emptyOptionLabel !== undefined) {
    return [{ label: emptyOptionLabel, value: "" }, ...BinMaterials];
  }

  return BinMaterials;
};

export const getBinSize = (
  task: TasksType,
  displaySource?: "default" | "driverPanel",
) => {
  return task.taskId === ServiceTaskTypeEnum.DROPBIN ||
    task.taskId === ServiceTaskTypeEnum.EXCHANGEBIN ||
    task.taskId === ServiceTaskTypeEnum.WAITANDLOADBIN
    ? formatBinSize(task.size, displaySource)
    : task.connectedTask
      ? formatBinSize(task.connectedTask?.size)
      : i18n.t("driver_task_card.size_tbd");
};

export const sortBinsByIdentifier = (bins: BinType[]) => {
  return bins.sort((a, b) => (a.identifier > b.identifier ? 1 : -1));
};
